import { z } from 'zod';
import { AdminReason, IsoDateTime, PackKey } from './fields.js';
/**
 * Mocks, attempts and ranking contracts (M4). Times are ISO strings; scores are marks as in
 * the exam pack (negative marking applied).
 */
export const MockStatusName = z.enum([
    'draft',
    'scheduled',
    'open',
    'closed',
    'results_published',
    'archived',
]);
export const RagStatus = z.enum(['green', 'amber', 'red']);
// ---- requests --------------------------------------------------------------------------------
export const CreateMockRequest = z
    .object({
    examKey: PackKey,
    stageKey: PackKey,
    paperKey: PackKey,
    branchKey: PackKey.nullish(),
    levelKey: PackKey.nullish(),
    variantKey: PackKey.nullish(),
    sectionKey: z.string().max(64).nullish(),
    patternKey: PackKey,
    title: z.string().trim().min(3).max(160).optional(),
    windowStart: IsoDateTime.optional(),
    windowEnd: IsoDateTime.optional(),
    resultsAt: IsoDateTime.optional(),
    forms: z.number().int().min(1).max(10).optional(),
    betaRanked: z.boolean().optional(),
    /** Share of real SSC PYQs, 0–100 (needs pyq_in_mocks; ranked ≤ 30; Rankers League 0). */
    pyqShare: z.number().int().min(0).max(100).optional(),
    mirror: z
        .object({
        year: z.union([z.number(), z.string()]).optional(),
        shift: z.union([z.number(), z.string()]).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const MockListQuery = z.object({
    status: MockStatusName.optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
/**
 * Build (or rebuild) a draft mock. `allowReuse`: when the bank is short, let questions from the
 * oldest frozen mocks of the exam back in (otherwise a mock never repeats another's questions).
 */
export const BuildMockRequest = z.object({ allowReuse: z.boolean().optional() }).strict();
export const MockTransitionRequest = z
    .object({ event: z.enum(['open', 'close', 'archive', 'publish_results']) })
    .strict();
export const RescoreRequest = z
    .object({
    itemId: z.uuid(),
    decision: z.enum(['bonus', 'drop', 'keep']),
    reason: AdminReason,
})
    .strict();
export const FeasibilityQuery = z.object({
    mocks: z.coerce.number().int().min(1).max(100).optional(),
});
export const MockQuestionsQuery = z.object({
    form: z.coerce.number().int().min(0).max(9).optional(),
});
// ---- responses -------------------------------------------------------------------------------
export const FeasibilityRowDto = z.object({
    sectionKey: z.string(),
    subject: z.string(),
    topicRoot: z.string(),
    band: z.string().nullable(),
    need: z.number(),
    have: z.number(),
    status: RagStatus,
});
export const FeasibilityDto = z.object({
    status: RagStatus,
    rows: z.array(FeasibilityRowDto),
});
export const MockDto = z.object({
    id: z.uuid(),
    productId: z.uuid(),
    title: z.string(),
    examKey: z.string(),
    stageKey: z.string(),
    paperKey: z.string(),
    branchKey: z.string().nullable(),
    levelKey: z.string().nullable(),
    sectionKey: z.string().nullable(),
    patternKey: z.string(),
    blueprintMethod: z.string(),
    status: MockStatusName,
    ranked: z.boolean(),
    betaRanked: z.boolean(),
    forms: z.number(),
    windowStart: z.string().nullable(),
    windowEnd: z.string().nullable(),
    resultsAt: z.string().nullable(),
    built: z.boolean(),
    shortfalls: z.array(z.object({ sectionKey: z.string(), need: z.number(), got: z.number(), form: z.number() })),
    questions: z.number(),
    durationMin: z.number().nullable(),
    feasibility: FeasibilityDto.nullable(),
    pyqShare: z.number().optional(),
    pyqMix: z
        .array(z.object({ form: z.number(), sectionKey: z.string(), want: z.number(), got: z.number() }))
        .optional(),
    createdAt: z.string(),
});
export const MockListDto = z.object({ mocks: z.array(MockDto) });
export const MockDetailDto = z.object({
    mock: MockDto,
    attempts: z.record(z.string(), z.number()),
    decisions: z.array(z.object({ itemId: z.uuid(), decision: z.string(), reason: z.string(), at: z.string() })),
    snapshot: z
        .object({
        id: z.uuid(),
        computedAt: z.string(),
        cohort: z.number(),
        final: z.boolean(),
        method: z.string(),
    })
        .nullable(),
});
export const FreezeDto = z.object({ mock: MockDto, spotCheck: z.array(z.uuid()) });
export const MockQuestionsDto = z.object({
    sections: z.array(z.object({
        key: z.string(),
        subjectName: z.string(),
        items: z.array(z.object({
            n: z.number(),
            itemId: z.uuid(),
            topicPath: z.string(),
            key: z.number(),
            stem: z.string(),
            status: z.string(),
        })),
    })),
});
export const RescoreResultDto = z.object({ affected: z.number() });
export const FeasibilityMatrixDto = z.object({
    mocks: z.number(),
    coldStart: z.boolean(),
    papers: z.array(z.object({
        examKey: z.string(),
        stageKey: z.string(),
        paperKey: z.string(),
        branchKey: z.string().nullable().optional(),
        levelKey: z.string().nullable().optional(),
        launch: z.string(),
        name: z.string(),
        status: RagStatus,
        sections: z.array(FeasibilityRowDto),
    })),
});
export const LeaderboardDto = z.object({
    snapshot: z
        .object({ id: z.uuid(), computedAt: z.string(), cohort: z.number(), final: z.boolean() })
        .nullable(),
    rows: z.array(z.object({
        attemptId: z.uuid(),
        rank: z.number(),
        percentile: z.number(),
        score: z.number(),
        userId: z.uuid(),
        firstName: z.string().nullable(),
    })),
});
//# sourceMappingURL=assessment.js.map